import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { formatMoney } from '@theme/money-formatting';

/**
 * Dynamic related-products upsell slider for the cart drawer.
 * Fetches recommendations (Shopify Product Recommendations API) based on the
 * last product in the cart and lets shoppers add single-variant products inline.
 */
class CalinCartUpsell extends HTMLElement {
  /** @type {((event: Event) => void) | null} */
  #onCartUpdate = null;

  /** @type {((event: Event) => void) | null} */
  #onDrawerOpen = null;

  /** @type {string} */
  #renderToken = '';

  /** @type {any[]} */
  #products = [];

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {((event: Event) => void) | null} */
  #onScroll = null;

  /** @type {Element | null} */
  track = null;

  /** @type {Element | null} */
  nav = null;

  /** @type {Element | null} */
  prevBtn = null;

  /** @type {Element | null} */
  nextBtn = null;

  /** @type {number} */
  limit = 6;

  /** @type {string} */
  intent = 'related';

  /** @type {string} */
  recommendationsUrl = '/recommendations/products';

  /** @type {string} */
  moneyFormat = '{{amount}}';

  /** @type {string} */
  addLabel = 'Add';

  /** @type {string} */
  soldOutLabel = 'Sold out';

  connectedCallback() {
    this.track = this.querySelector('.calin-cart-upsell__track');
    this.nav = this.querySelector('.calin-cart-upsell__nav');
    this.prevBtn = this.querySelector('[data-upsell-prev]');
    this.nextBtn = this.querySelector('[data-upsell-next]');
    this.limit = parseInt(this.dataset.limit || '6', 10);
    this.intent = this.dataset.intent || 'related';
    this.recommendationsUrl = this.dataset.recommendationsUrl || '/recommendations/products';
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.addLabel = this.dataset.addLabel || 'Add';
    this.soldOutLabel = this.dataset.soldoutLabel || 'Sold out';

    this.#refresh({
      baseProductId: this.dataset.baseProductId || '',
      cartProductIds: (this.dataset.cartProductIds || '').split(',').filter(Boolean),
    });

    this.#onCartUpdate = (event) => {
      // The cart event is dispatched optimistically, before the server mutation
      // finishes. Wait for its promise so /cart.js reflects the updated cart;
      // otherwise we'd fetch stale data and the upsell wouldn't update.
      const promise = /** @type {any} */ (event).promise;
      if (promise && typeof promise.then === 'function') {
        promise.then(() => this.#refreshFromCart()).catch(() => {});
      } else {
        this.#refreshFromCart();
      }
    };
    this.#onDrawerOpen = (event) => {
      const target = /** @type {HTMLElement | null} */ (event.target);
      if (target?.id !== 'cart-drawer') return;
      this.#refreshFromCart();
    };

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen);

    this.prevBtn?.addEventListener('click', () => this.#scrollByCard(-1));
    this.nextBtn?.addEventListener('click', () => this.#scrollByCard(1));
    this.#onScroll = () => this.#updateNavState();
    this.track?.addEventListener('scroll', this.#onScroll, { passive: true });

    // The cart drawer re-renders (morphs) on every cart change, which wipes the
    // client-rendered cards inside this element. Re-paint from cache whenever the
    // track is emptied by a morph while we still have products to show.
    if (this.track) {
      this.#observer = new MutationObserver(() => {
        if (
          this.#products.length > 0 &&
          this.track &&
          this.track.children.length === 0 &&
          !this.classList.contains('hidden')
        ) {
          this.#renderCards(this.#products);
        }
      });
      this.#observer.observe(this.track, { childList: true });
    }
  }

  disconnectedCallback() {
    if (this.#onCartUpdate) {
      document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    }
    if (this.#onDrawerOpen) {
      document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen);
    }
    if (this.#onScroll) {
      this.track?.removeEventListener('scroll', this.#onScroll);
    }
    this.#observer?.disconnect();
  }

  /**
   * Scrolls the track by roughly one card in the given direction.
   * @param {number} direction - -1 for previous, 1 for next.
   */
  #scrollByCard(direction) {
    if (!this.track) return;
    const card = this.track.querySelector('.calin-cart-upsell__card');
    const gap = parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;
    const amount = card ? card.getBoundingClientRect().width + gap : this.track.clientWidth * 0.8;
    this.track.scrollBy({ left: amount * direction, behavior: 'smooth' });
  }

  #updateNavState() {
    if (!this.track) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    // Skip while the drawer/track isn't laid out yet (clientWidth 0), otherwise
    // we'd set a wrong disabled state before real dimensions are available.
    if (clientWidth === 0) return;
    const maxScroll = scrollWidth - clientWidth;
    this.prevBtn?.toggleAttribute('disabled', scrollLeft <= 1);
    this.nextBtn?.toggleAttribute('disabled', scrollLeft >= maxScroll - 1);
  }

  async #refreshFromCart() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      const cart = /** @type {any} */ (await response.json());
      if (!cart.items || cart.items.length === 0) {
        this.#hide();
        return;
      }
      const baseProductId = String(cart.items[cart.items.length - 1].product_id);
      const cartProductIds = cart.items.map((/** @type {any} */ item) => String(item.product_id));
      this.#refresh({ baseProductId, cartProductIds });
    } catch {
      /* ignore network errors — keep last rendered state */
    }
  }

  /**
   * @param {{ baseProductId: string, cartProductIds: string[] }} params
   */
  async #refresh({ baseProductId, cartProductIds }) {
    if (!baseProductId) {
      this.#hide();
      return;
    }

    const token = `${baseProductId}|${cartProductIds.join(',')}`;

    // Only hit the network when the cart context actually changed; otherwise
    // re-paint from the cached products (e.g. after a morph wiped the track).
    if (token !== this.#renderToken) {
      this.#renderToken = token;
      try {
        const fetchLimit = Math.min(10, this.limit + cartProductIds.length);
        const url =
          `${this.recommendationsUrl}.json` +
          `?product_id=${encodeURIComponent(baseProductId)}` +
          `&limit=${fetchLimit}` +
          `&intent=${encodeURIComponent(this.intent)}`;

        const response = await fetch(url, { headers: { Accept: 'application/json' } });
        const data = /** @type {any} */ (await response.json());

        const exclude = new Set(cartProductIds.map(String));
        this.#products = (data.products || [])
          .filter((/** @type {any} */ product) => !exclude.has(String(product.id)))
          .slice(0, this.limit);
      } catch {
        this.#hide();
        return;
      }
    }

    if (this.#products.length === 0) {
      this.#hide();
      return;
    }

    this.#renderCards(this.#products);
    this.classList.remove('hidden');
  }

  #hide() {
    this.#products = [];
    this.classList.add('hidden');
    this.track?.replaceChildren();
  }

  /**
   * @param {any[]} products
   */
  #renderCards(products) {
    if (!this.track) return;
    const currency = window.Shopify?.currency?.active || '';
    const fragment = document.createDocumentFragment();
    for (const product of products) {
      fragment.appendChild(this.#createCard(product, currency));
    }
    this.track.replaceChildren(fragment);
    this.#updateNavState();
    requestAnimationFrame(() => this.#updateNavState());
  }

  /**
   * @param {any} product
   * @param {string} currency
   */
  #createCard(product, currency) {
    const card = document.createElement('div');
    card.className = 'calin-cart-upsell__card';
    card.setAttribute('role', 'listitem');

    const url = product.url || `/products/${product.handle}`;
    const imageSrc = this.#imageUrl(product.featured_image || (product.images && product.images[0]));

    const imageLink = document.createElement('a');
    imageLink.className = 'calin-cart-upsell__image-link';
    imageLink.href = url;
    if (imageSrc) {
      const img = document.createElement('img');
      img.className = 'calin-cart-upsell__image';
      img.src = imageSrc;
      img.alt = product.title || '';
      img.loading = 'lazy';
      imageLink.appendChild(img);
    }
    card.appendChild(imageLink);

    const info = document.createElement('div');
    info.className = 'calin-cart-upsell__info';

    const titleLink = document.createElement('a');
    titleLink.className = 'calin-cart-upsell__title';
    titleLink.href = url;
    titleLink.textContent = product.title || '';
    info.appendChild(titleLink);

    const prices = document.createElement('div');
    prices.className = 'calin-cart-upsell__prices';

    const price = document.createElement('span');
    price.className = 'calin-cart-upsell__price';
    price.textContent = formatMoney(product.price, this.moneyFormat, currency);
    prices.appendChild(price);

    const compare = product.compare_at_price ?? product.compare_at_price_min;
    if (compare && compare > product.price) {
      const compareEl = document.createElement('span');
      compareEl.className = 'calin-cart-upsell__price--compare';
      compareEl.textContent = formatMoney(compare, this.moneyFormat, currency);
      prices.appendChild(compareEl);
    }
    info.appendChild(prices);

    const variants = /** @type {any[]} */ (product.variants || []);
    const firstAvailable = variants.find((variant) => variant.available) || variants[0];

    // Track which variant the Add button will add. Updated by the dropdown.
    const selection = { variantId: firstAvailable ? firstAvailable.id : null };

    const actions = document.createElement('div');
    actions.className = 'calin-cart-upsell__actions';

    if (variants.length > 1) {
      const select = document.createElement('select');
      select.className = 'calin-cart-upsell__variant-select';
      select.setAttribute('aria-label', product.title || '');
      for (const variant of variants) {
        const option = document.createElement('option');
        option.value = String(variant.id);
        option.textContent = variant.available
          ? variant.title
          : `${variant.title} - ${this.soldOutLabel}`;
        if (!variant.available) option.disabled = true;
        if (firstAvailable && variant.id === firstAvailable.id) option.selected = true;
        select.appendChild(option);
      }
      select.addEventListener('change', () => {
        selection.variantId = select.value;
      });
      actions.appendChild(select);
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'calin-cart-upsell__add';
    button.textContent = this.addLabel;
    if (firstAvailable && firstAvailable.available) {
      button.addEventListener('click', () => {
        if (selection.variantId != null) this.#addToCart(selection.variantId, button);
      });
    } else {
      button.disabled = true;
    }
    actions.appendChild(button);
    info.appendChild(actions);

    card.appendChild(info);

    return card;
  }

  /**
   * @param {string | undefined} src
   */
  #imageUrl(src) {
    if (!src) return '';
    const clean = src.startsWith('//') ? `https:${src}` : src;
    return `${clean}${clean.includes('?') ? '&' : '?'}width=300`;
  }

  /**
   * @param {number|string} variantId
   * @param {HTMLButtonElement} button
   */
  async #addToCart(variantId, button) {
    button.setAttribute('aria-busy', 'true');

    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((element) => {
      const id = element.getAttribute('data-section-id');
      if (id) sectionIds.add(id);
    });

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const addResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: Array.from(sectionIds).join(','),
          sections_url: window.location.pathname,
        }),
      });
      const addData = await addResponse.json();

      if (addData.status) {
        deferred.reject(new Error(addData.message || 'Add to cart failed'));
        return;
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: addData.sections,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'calin-cart-upsell',
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);
    } finally {
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('calin-cart-upsell')) {
  customElements.define('calin-cart-upsell', CalinCartUpsell);
}
